import type React from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  DeleteOutlineRounded,
  GroupsOutlined,
  VideocamOutlined,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import CustomSelect from "../../../../../components/ui/Select";
import CustomTextField from "../../../../../components/ui/Textfield";
import {
  newRequirementRow,
  type CrewRequirementDraft,
  type PackageBuilderState,
  type RoleOption,
} from "../types";

interface Props {
  state: PackageBuilderState;
  update: (patch: Partial<PackageBuilderState>) => void;
  roleOptions: RoleOption[];
  rolesLoading: boolean;
}

const OperationalRequirementsTab: React.FC<Props> = ({
  state,
  update,
  roleOptions,
  rolesLoading,
}) => {
  const rows = state.crew_requirements;

  const patchRow = (key: string, patch: Partial<CrewRequirementDraft>) =>
    update({
      crew_requirements: rows.map((r) =>
        r.key === key ? { ...r, ...patch } : r,
      ),
    });

  const addRow = () =>
    update({ crew_requirements: [...rows, newRequirementRow()] });

  const removeRow = (key: string) =>
    update({ crew_requirements: rows.filter((r) => r.key !== key) });

  const usedRoleIds = new Set(
    rows.map((r) => r.role_id).filter((v): v is number => v !== ""),
  );
  const totalCrew = rows.reduce(
    (sum, r) => sum + (r.role_id === "" ? 0 : Number(r.quantity) || 0),
    0,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 860 }}>
      {/* Crew requirements */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <GroupsOutlined sx={{ fontSize: 20, color: "#6b4226" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Crew Requirements
          </Typography>
          {totalCrew > 0 && (
            <Chip
              size="small"
              label={`${totalCrew} crew · ${rows.filter((r) => r.role_id !== "").length} role${
                rows.filter((r) => r.role_id !== "").length === 1 ? "" : "s"
              }`}
              sx={{ height: 20, fontSize: 11 }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
          When a stream is sold on this package, the call sheet will flag these
          roles and recommend crew for each. Manage the role catalogue in the{" "}
          <RouterLink to="/configurations/package/requirements-registry">
            Requirements Registry
          </RouterLink>
          .
        </Typography>

        {rows.length === 0 && (
          <Box
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: "12px",
              p: 3,
              textAlign: "center",
              color: "text.secondary",
              fontSize: 13,
            }}
          >
            No crew requirements yet. Add the roles this package needs.
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rows.map((row) => (
            <Box
              key={row.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
              }}
            >
              <Box sx={{ flex: 2, minWidth: 180 }}>
                <CustomSelect
                  label="Role"
                  value={row.role_id === "" ? "" : String(row.role_id)}
                  disabled={rolesLoading}
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    patchRow(row.key, {
                      role_id: id,
                      role_name: roleOptions.find((r) => r.id === id)?.name,
                    });
                  }}
                >
                  {roleOptions.map((r) => (
                    <MenuItem
                      key={r.id}
                      value={String(r.id)}
                      disabled={usedRoleIds.has(r.id) && r.id !== row.role_id}
                    >
                      {r.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Box>

              <Box sx={{ width: 110 }}>
                <CustomTextField
                  label="Qty"
                  type="number"
                  value={row.quantity}
                  onChange={(e) =>
                    patchRow(row.key, {
                      quantity: Math.max(1, Number(e.target.value) || 1),
                    })
                  }
                  inputProps={{ min: 1 }}
                />
              </Box>

              <FormControlLabel
                sx={{ whiteSpace: "nowrap" }}
                control={
                  <Switch
                    checked={row.is_mandatory}
                    onChange={(e) =>
                      patchRow(row.key, { is_mandatory: e.target.checked })
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#6b4226" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: "#6b4226",
                      },
                    }}
                  />
                }
                label="Mandatory"
              />

              <IconButton
                onClick={() => removeRow(row.key)}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <DeleteOutlineRounded fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Button
          onClick={addRow}
          startIcon={<AddRounded />}
          sx={{ mt: 1.5, textTransform: "none", color: "#6b4226" }}
        >
          Add role requirement
        </Button>
      </Box>

      {/* Gear requirements — not yet available */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          p: 2.5,
          opacity: 0.6,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <VideocamOutlined sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Gear Requirements
          </Typography>
          <Chip size="small" label="Coming soon" sx={{ height: 20, fontSize: 11 }} />
        </Box>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
          Auto-allocation of cameras and other equipment to a stream based on its
          package will be configured here.
        </Typography>
      </Box>
    </Box>
  );
};

export default OperationalRequirementsTab;
